import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import {
  studentFormsApi,
  type StudentForm,
  type StudentFormAnswerValue,
} from '@/api/studentForms'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { StudentFormFieldInput } from './StudentFormFieldInput'
import { StudentFormHistoryDialog } from './StudentFormHistoryDialog'

interface StudentFormEditDialogProps {
  open: boolean
  contractId: number
  /** Уже загруженная анкета — модалка открывается сразу, без ожидания запроса. */
  form: StudentForm | null
  /** Учитель ведёт анкету: правит все поля и смотрит историю правок. */
  canManage: boolean
  onClose: () => void
  onSaved: (form: StudentForm) => void
}

/**
 * Редактирование анкеты ученика в модальном окне — как правка договора.
 *
 * Состав полей и права приходят с бэкенда: ученику поля `student_visible`
 * доступны только для чтения, `teacher_only` не приходят вовсе.
 */
export function StudentFormEditDialog({
  open,
  contractId,
  form,
  canManage,
  onClose,
  onSaved,
}: StudentFormEditDialogProps) {
  const { t } = useTranslation()

  const [values, setValues] = useState<Record<string, StudentFormAnswerValue>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    const map: Record<string, StudentFormAnswerValue> = {}
    for (const answer of form?.answers ?? []) {
      map[answer.field_key] = answer.value
    }
    setValues(map)
    setError(null)
  }, [open, form])

  const fields = form?.fields ?? []
  // Ученику доступны для правки только student_editable; учителю — всё.
  const hasEditableFields = canManage
    ? fields.length > 0
    : fields.some((field) => field.access === 'student_editable')

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      onSaved(await studentFormsApi.saveAnswers(contractId, values))
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('studentForms.saveError')))
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flexGrow: 1 }}>{t('studentForms.title')}</Box>
          {canManage && form?.exists && (
            <Button size="small" onClick={() => setHistoryOpen(true)}>
              {t('studentForms.history')}
            </Button>
          )}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            {fields.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('studentForms.noFields')}
              </Typography>
            ) : (
              fields.map((field) => (
                <StudentFormFieldInput
                  key={field.key}
                  field={field}
                  value={values[field.key] ?? null}
                  readOnly={!canManage && field.access !== 'student_editable'}
                  onChange={(next) => setValues((prev) => ({ ...prev, [field.key]: next }))}
                />
              ))
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          {hasEditableFields && (
            <Button variant="contained" disabled={saving} onClick={() => void handleSave()}>
              {saving ? t('common.saving', 'Сохранение...') : t('common.save', 'Сохранить')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <StudentFormHistoryDialog
        open={historyOpen}
        contractId={contractId}
        onClose={() => setHistoryOpen(false)}
      />
    </>
  )
}
